import React from 'react';
import {
  Box,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SalovareSelectField from '../common/SalovareSelectField';
import { formatUtcDateTime } from '../../utils/dateFormatUtils';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatQuantityNumber, toPackageQuantity } from '../../utils/stockQuantityUtils';
import { STOCK_MOVEMENT_TYPES } from '../../constants/Stock';
import { BRAND } from '../../theme/brandTokens';

const StockMovementsPanel = ({
  items = [],
  totalCount = 0,
  page,
  pageSize,
  movementType,
  onMovementTypeChange,
  onPageChange,
  canSeeCosts = false,
  currency,
}) => {
  const { t } = useTranslation();

  return (
    <Box id="stock-movements-panel" sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ maxWidth: 280, mb: 2 }}>
        <SalovareSelectField
          id="stock-movement-type"
          label={t('stock.movements.type')}
          value={movementType}
          displayEmpty
          onChange={(event) => onMovementTypeChange(event.target.value)}
        >
          <MenuItem value="">{t('stock.movements.allTypes')}</MenuItem>
          {STOCK_MOVEMENT_TYPES.map((type) => (
            <MenuItem key={type} value={type}>{t(`stock.movementTypes.${type}`)}</MenuItem>
          ))}
        </SalovareSelectField>
      </Box>
      <Box sx={{ overflowX: 'auto', border: `1px solid ${BRAND.line}`, borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: BRAND.background }}>
              <TableCell>{t('stock.movements.when')}</TableCell>
              <TableCell>{t('stock.movements.product')}</TableCell>
              <TableCell>{t('stock.movements.type')}</TableCell>
              <TableCell align="right">{t('stock.movements.delta')}</TableCell>
              <TableCell align="right">{t('stock.movements.balance')}</TableCell>
              {canSeeCosts ? <TableCell align="right">{t('stock.movements.cost')}</TableCell> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canSeeCosts ? 6 : 5}>
                  <Typography variant="body2" color="text.secondary">{t('stock.movements.empty')}</Typography>
                </TableCell>
              </TableRow>
            ) : items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{formatUtcDateTime(item.occurredAtUtc)}</TableCell>
                <TableCell>{item.productName}</TableCell>
                <TableCell>
                  {t(`stock.movementTypes.${item.movementType}`)}
                  {item.reason ? ` · ${t(`stock.reasons.${item.reason}`)}` : ''}
                </TableCell>
                <TableCell align="right">{formatQuantityNumber(toPackageQuantity(item.quantityDelta, item.contentQuantity))}</TableCell>
                <TableCell align="right">{formatQuantityNumber(toPackageQuantity(item.balanceAfter, item.contentQuantity))}</TableCell>
                {canSeeCosts ? (
                  <TableCell align="right">
                    {item.unitCost == null
                      ? '—'
                      : formatCurrency(item.unitCost, currency)}
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={Math.max(0, page - 1)}
          onPageChange={(_, nextPage) => onPageChange(nextPage + 1)}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[pageSize]}
          onRowsPerPageChange={() => {}}
        />
      </Box>
    </Box>
  );
};

export default StockMovementsPanel;
